'use client';
import { useState, useEffect } from 'react';
import { AthleteSettings, getAthleteSettings, saveAthleteSettings, DEFAULT_SETTINGS } from '@/lib/athleteSettings';
import { X, Save, User } from 'lucide-react';

export function AthleteSettingsForm({ onSave, onComplete }: { onSave: (settings: AthleteSettings) => void; onComplete?: () => void; }) {
  const [settings, setSettings] = useState<AthleteSettings>(DEFAULT_SETTINGS);

  useEffect(() => {
    getAthleteSettings().then(setSettings);
  }, []);

  return (
    <div className="flex flex-col gap-4 p-5 rounded-2xl bg-card border border-border/60">
      <div>
        <p className="text-sm font-semibold tracking-tight">Athlete Profile</p>
        <p className="text-xs text-muted-foreground mt-0.5">Update your metrics to dynamically recalculate training zones.</p>
      </div>
      
      <div className="space-y-4 mt-2">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] uppercase font-bold text-muted-foreground mb-1">Weight (kg)</label>
            <input type="number" value={settings.weight} onChange={e => setSettings({...settings, weight: Number(e.target.value)})} className="w-full bg-background border border-border/50 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:border-foreground transition-all" />
          </div>
          <div>
            <label className="block text-[10px] uppercase font-bold text-muted-foreground mb-1">Height (cm)</label>
            <input type="number" value={settings.height} onChange={e => setSettings({...settings, height: Number(e.target.value)})} className="w-full bg-background border border-border/50 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:border-foreground transition-all" />
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] uppercase font-bold text-muted-foreground mb-1">FTP (Watts)</label>
            <input type="number" value={settings.ftp} onChange={e => setSettings({...settings, ftp: Number(e.target.value)})} className="w-full bg-background border border-border/50 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:border-foreground transition-all" />
          </div>
          <div>
            <label className="block text-[10px] uppercase font-bold text-muted-foreground mb-1">Max HR (bpm)</label>
            <input type="number" value={settings.maxHr} onChange={e => setSettings({...settings, maxHr: Number(e.target.value)})} className="w-full bg-background border border-border/50 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:border-foreground transition-all" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] uppercase font-bold text-muted-foreground mb-1">LTHR (bpm)</label>
            <input type="number" value={settings.lthr} onChange={e => setSettings({...settings, lthr: Number(e.target.value)})} className="w-full bg-background border border-border/50 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:border-foreground transition-all" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] uppercase font-bold text-muted-foreground mb-1">CSS (min:sec)</label>
            <input type="text" value={settings.css} onChange={e => setSettings({...settings, css: e.target.value})} className="w-full bg-background border border-border/50 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:border-foreground transition-all" placeholder="1:42" />
          </div>
          <div>
            <label className="block text-[10px] uppercase font-bold text-muted-foreground mb-1">Run Thresh (min:sec)</label>
            <input type="text" value={settings.runThreshold} onChange={e => setSettings({...settings, runThreshold: e.target.value})} className="w-full bg-background border border-border/50 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:border-foreground transition-all" placeholder="5:00" />
          </div>
        </div>
      </div>

      <button 
        onClick={async () => { await saveAthleteSettings(settings); onSave(settings); if(onComplete) onComplete(); }} 
        className="w-full mt-2 bg-foreground text-background hover:opacity-90 font-medium text-sm rounded-lg px-4 py-2 flex items-center justify-center gap-2 transition-all"
      >
        <Save className="w-4 h-4" /> Save Profile
      </button>
    </div>
  );
}
